import { useCallback, useEffect } from "react";

import { EntityId } from "@reduxjs/toolkit";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { playbackActions } from "./store/slice";
import { playbackSelectionSelectors } from "./store/selectors.selection";
import { selectPlaybackConfiguration } from "features/config/store/selectors";
import { selectPagedDevicesBySiteId } from "features/devices/store/selectors";
import { devicesThunks } from "features/devices/store/slice";

import { useReflectUrlParamWithState } from "hooks/useReflectUrlParamWithState";
import { useUrlSearchParamsDateTimeRange } from "./hooks/useUrlSearchParamsDateTimeRange";

import { Sidebar } from "./Sidebar";
import { PlaybackSession } from "./PlaybackSession";
import { DefaultPageLayout } from "features/core/DefaultPageLayout";
import { Alert } from "@mui/material";

export interface PlaybackProps {
    drawerWidth: number;
}

export function Playback(props: PlaybackProps) {
    const dispatch = useAppDispatch();

    // configuration
    const playbackConfiguration = useAppSelector(selectPlaybackConfiguration);

    // site selection
    const siteId = useReflectUrlParamWithState(
        playbackSelectionSelectors.selectSiteId,
        (urlParamValue: string) =>
            dispatch(
                playbackActions.setSiteId({
                    siteId: urlParamValue,
                }),
            ),
        "siteId",
        "/playback/:siteId",
        true,
    );

    // date range
    const onDateTimeRangeChanged = useCallback(
        (startDateTime: string, endDateTime: string) => {
            dispatch(
                playbackActions.setStartAndEndDateTime({
                    startDateTime: startDateTime,
                    endDateTime: endDateTime,
                }),
            );
        },
        [dispatch],
    );

    const { onUpdateDateRange } = useUrlSearchParamsDateTimeRange(
        "start",
        "end",
        15,
        "minutes",
        onDateTimeRangeChanged,
    );

    // sidebar
    const rangeStartDateTime = useAppSelector(
        playbackSelectionSelectors.selectStartDateTime,
    );
    const rangeStartDateTimeError = useAppSelector(
        playbackSelectionSelectors.selectStartDateTimeError,
    );
    const rangeEndDateTime = useAppSelector(
        playbackSelectionSelectors.selectEndDateTime,
    );
    const rangeEndDateTimeError = useAppSelector(
        playbackSelectionSelectors.selectEndDateTimeError,
    );

    // retrieve site devices
    useEffect(() => {
        if (siteId) {
            dispatch(devicesThunks.fetchDevicesForSiteAsync(siteId));
        }
    }, [siteId, dispatch]);

    // retrieve the default device selection for the current site
    const defaultDeviceSelectionForSite = useAppSelector((state) => {
        if (!siteId || !playbackConfiguration?.maxDefaultSelectionSize)
            return undefined;

        const pageNumber = 1;

        return selectPagedDevicesBySiteId(
            state,
            siteId,
            pageNumber,
            playbackConfiguration.maxDefaultSelectionSize,
        );
    });

    // playback session
    const hasPlaybackSessionRequirementsFulfilled = useAppSelector(
        playbackSelectionSelectors.selectHasPlaybackSessionRequirementsFulfilled,
    );

    return (
        <DefaultPageLayout
            drawerWidth={props.drawerWidth}
            sidebar={
                siteId && (
                    <Sidebar
                        onToggleDevice={(
                            siteId: EntityId,
                            deviceId: EntityId,
                            selected: boolean,
                        ) =>
                            dispatch(
                                playbackActions.upsertDeviceSelectionState({
                                    deviceId,
                                    selected,
                                }),
                            )
                        }
                        onSetSelectionStateOfAllDevicesInSite={(
                            siteId: EntityId,
                            selected: boolean,
                        ) => {
                            dispatch(
                                playbackActions.setSelectedDevices({
                                    deviceIds: selected
                                        ? (
                                              defaultDeviceSelectionForSite ??
                                              []
                                          ).map((d) => d.id)
                                        : [],
                                }),
                            );
                        }}
                        siteId={siteId}
                        rangeStartDateTime={rangeStartDateTime}
                        rangeStartDateTimeError={rangeStartDateTimeError}
                        rangeEndDateTime={rangeEndDateTime}
                        rangeEndDateTimeError={rangeEndDateTimeError}
                        onRangeChange={onUpdateDateRange}
                    />
                )
            }
            main={
                <>
                    {!siteId && (
                        <Alert severity="info">Please select a site</Alert>
                    )}

                    {hasPlaybackSessionRequirementsFulfilled &&
                        playbackConfiguration &&
                        rangeStartDateTime &&
                        rangeEndDateTime &&
                        siteId && (
                            <PlaybackSession
                                siteId={siteId}
                                startDateTime={rangeStartDateTime}
                                endDateTime={rangeEndDateTime}
                                playbackConfiguration={playbackConfiguration}
                                drawerWidth={props.drawerWidth}
                            />
                        )}
                </>
            }
        />
    );
}
